import { ContentLoader, EditTemplateIcon, EmptyState, IconButton, Pagination, Paper, SearchInput, SelectTemplateIcon, Typography, ViewTemplateIcon } from '@dev/ee-components';
import { useTranslation } from 'react-i18next';
import { ButtonBase } from '@material-ui/core';
import { getApiPath } from '@/services/Secuirty';
import React from 'react';
import { ItemsGalleryItemAction, ItemsGalleryProps } from './types';
import { ItemsGalleryButtonIcon, ItemsGalleryButtons, ItemsGalleryItem, ItemsGalleryItemName, ItemsGalleryItems, ItemsGalleryScreenshot, ItemsGalleryScreenshotWrapper, SearchInputWrapper } from './styles';
import { defaultPerPageDropdownItems } from './constants';

export const ItemsGallery = ({
    isBusy,
    isMobile,
    minItemWidth = '246px',
    items,
    itemsTypeName,
    emptyMessage,
    activePage,
    itemsPerPage,
    itemsTotalCount,
    searchValue,
    searchPlaceholder,
    onSearchChange,
    onSearchDelete,
    perPageDropdownItems = defaultPerPageDropdownItems,
    onPageChange,
    onItemPreview,
    onItemSelect,
    onItemEdit
}: ItemsGalleryProps): JSX.Element => {
    const { t } = useTranslation();
    
    const actions: ItemsGalleryItemAction[] = [
        ...(onItemPreview ? [{
            id: 'items-gallery-view-action',
            icon: <ViewTemplateIcon />,
            label: t('view'),
            onClick: onItemPreview,
        }]: []),
        ...(onItemSelect ? [{
            id: 'items-gallery-select-action',
            icon: <SelectTemplateIcon />,
            label: t('select'),
            onClick: onItemSelect,
        }]: []),
        ...(onItemEdit ? [{
            id: 'items-gallery-edit-action',
            icon: <EditTemplateIcon />,
            label: t('edit'),
            onClick: onItemEdit,
        }]: [])
    ];

    const placeholderItems = new Array(itemsPerPage).fill(1);

    const isListVisible = isBusy || itemsTotalCount > 0;

    return (
        <Paper>
            <SearchInputWrapper $isMobile={isMobile}>
                <SearchInput
                    id={'searchTemplates'}
                    name={'search'}
                    onChange={onSearchChange}
                    onDelete={onSearchDelete}
                    placeholder={searchPlaceholder}
                    type={'text'}
                    value={searchValue}
                    autoComplete="off"
                />
            </SearchInputWrapper>
            {isListVisible && <ItemsGalleryItems $isMobile={isMobile} $minItemWidth={minItemWidth}>
            {
                isBusy && placeholderItems.map((item, index) => (
                    <ItemsGalleryItem key={`placeholder-${index}`}>
                        <ItemsGalleryScreenshotWrapper><ContentLoader width="100%" count={1} mode="row" height="310px" /></ItemsGalleryScreenshotWrapper>
                        <ItemsGalleryItemName><ContentLoader count={1} mode="row" width="100px" height="20px" /></ItemsGalleryItemName>
                    </ItemsGalleryItem>
                ))
            }
            {
                !isBusy && itemsTotalCount > 0 && items.map((item) =>
                <ButtonBase
                    component='a'
                    key={item.id}
                    id={item.id.toString()}
                    focusRipple
                >
                    <ItemsGalleryItem>
                        <ItemsGalleryScreenshotWrapper>
                            {item.screenshot && <ItemsGalleryScreenshot src={`${getApiPath()}/userfile${item.screenshot}`} alt="" loading="lazy" />}
                            <ItemsGalleryButtons>
                                {actions.map(action => (
                                    <IconButton
                                        key={`${item.id}${action.id}`}
                                        secondary
                                        icon={<ItemsGalleryButtonIcon>{action.icon}{action.label}</ItemsGalleryButtonIcon>}
                                        onClick={() => action.onClick(item.name)}
                                    />
                                ))}
                            </ItemsGalleryButtons>
                        </ItemsGalleryScreenshotWrapper>
                        <ItemsGalleryItemName><Typography variant="h4">{item.name}</Typography></ItemsGalleryItemName>
                    </ItemsGalleryItem>
                </ButtonBase>
            )}
            </ItemsGalleryItems>}
            {!isBusy && itemsTotalCount === 0 && <EmptyState text={emptyMessage} />}
            <Pagination
                dataLength={itemsTotalCount}
                perPage={itemsPerPage}
                activePage={activePage}
                handlePaginationChange={onPageChange}
                itemsTranslation={itemsTypeName.toLowerCase()}
                siblingCount={1}
                boundaryCount={1}
                perPageOptions={perPageDropdownItems}
                perPageTranslation={t('perpage')}
                viewTranslation={t('view')}
                outOfTranslation={t('outof')}
            />
        </Paper>
    );
};